import { useQuery } from '@tanstack/react-query';
import { Controller, useFormContext } from 'react-hook-form';
import DropdownStyled from '../../../components/inputs/StyledInputs/DropdownStyled';
import FormErrorMessage from '../../../components/messages/FormErrorMessage';
import { fetchCompanies } from '../../../services/admin/UsersService';
import { getCompanyLabel } from '../../../../utils/formatUtils';
import { Company } from '../../../Interfaces/admin/User.interfaces';
import { getApprovalOrderSections } from '../../../services/admin/ApprovalSequences';
import { ApprovalSequenceFormFields } from '../../../Interfaces/admin/ApprovalSequence.interface';
import { formatReportLabel } from '@/src/utils/roleUtils';
import SequenceDetails from './SequencesDetails';

const Form = () => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<ApprovalSequenceFormFields>();

  const getCompany = useQuery({
    queryKey: ['getCompanies'],
    queryFn: ({ signal }) => fetchCompanies(undefined, signal),
    refetchOnWindowFocus: false,
  });

  const getApprovalOrderReports = useQuery({
    queryKey: ['getApprovalOrderReports'],
    queryFn: ({ signal }) => getApprovalOrderSections(signal),
    refetchOnWindowFocus: false,
  });

  return (
    <div className="grid mx-3 justify-content-center w-full">
      <div className="col-6">
        <label htmlFor="company" className="text-standard block mb-2">
          Company
        </label>
        <Controller
          defaultValue=""
          control={control}
          name="company"
          rules={{
            required: {
              value: true,
              message: 'Company is a required field',
            },
          }}
          render={({ field: { ref, onChange, ...otherFields } }) => (
            <DropdownStyled
              id="company"
              isLoading={getCompany.isLoading}
              isDisabled={getCompany.isError}
              options={getCompany.data?.companies.map((company: Company) => ({
                ...company,
                label: getCompanyLabel(company),
              }))}
              labelField="label"
              valueField="id"
              placeholder={
                getCompany.isError
                  ? 'Failed to load companies!'
                  : 'Select company'
              }
              clearable={false}
              inputRef={ref}
              error={!!errors['company']}
              filter={true}
              resetFilterOnHide={true}
              onChange={(e) => {
                onChange(e);
                setValue('sequence', [{ step: 1, members: [] }]);
              }}
              {...otherFields}
            />
          )}
        />
        {errors['company'] && (
          <FormErrorMessage
            errorMessage={errors['company'].message as string}
          />
        )}
      </div>
      <div className="col-6">
        <label htmlFor="report" className="text-standard block mb-2">
          Report
        </label>
        <Controller
          defaultValue=""
          control={control}
          name="report"
          rules={{
            required: {
              value: true,
              message: 'Report is a required field',
            },
          }}
          render={({ field: { ref, onChange, ...otherFields } }) => (
            <DropdownStyled
              id="report"
              isLoading={getApprovalOrderReports.isLoading}
              isDisabled={getApprovalOrderReports.isError}
              options={
                getApprovalOrderReports.data?.map((report) => ({
                  label: formatReportLabel(report),
                  id: report,
                })) ?? []
              }
              labelField="label"
              valueField="id"
              placeholder={
                getCompany.isError ? 'Failed to load reports!' : 'Select report'
              }
              clearable={false}
              inputRef={ref}
              error={!!errors['report']}
              filter={true}
              resetFilterOnHide={true}
              onChange={(e) => {
                onChange(e);
                setValue('sequence', [{ step: 1, members: [] }]);
              }}
              {...otherFields}
            />
          )}
        />
        {errors['report'] && (
          <FormErrorMessage errorMessage={errors['report'].message as string} />
        )}
      </div>
      <div className="col-12">
        <label htmlFor="report" className="text-standard block mb-2">
          Sequence
        </label>
        <SequenceDetails />
      </div>
    </div>
  );
};

export default Form;
